<script setup>
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'
import UModal from 'nvd-u/components/UModal.vue'

const props = defineProps({ modelValue: Boolean, title: String })
const emit = defineEmits(['update:modelValue', 'cancel', 'ok'])
const modal = ref(null)
let previousFocus = null

function close() {
    emit('cancel')
    emit('update:modelValue', false)
}

function handleKeydown(event) {
    if (!props.modelValue) return
    if (event.key === 'Escape') {
        event.preventDefault()
        event.stopImmediatePropagation()
        close()
    } else if (event.key === 'Tab') {
        const elements = [...(modal.value?.$el?.querySelectorAll('button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), a[href], [tabindex="0"]') || [])]
            .filter(element => element.getClientRects().length)
        const first = elements[0]
        const last = elements[elements.length - 1]
        if (event.shiftKey && (document.activeElement === first || !elements.includes(document.activeElement))) {
            event.preventDefault()
            last?.focus()
        } else if (!event.shiftKey && (document.activeElement === last || !elements.includes(document.activeElement))) {
            event.preventDefault()
            first?.focus()
        }
    }
}

watch(() => props.modelValue, async open => {
    document.removeEventListener('keydown', handleKeydown, true)
    if (open) {
        previousFocus = document.activeElement
        await nextTick()
        if (!props.modelValue) return
        document.addEventListener('keydown', handleKeydown, true)
        const root = modal.value?.$el
        const closeButton = root?.querySelector('.close-modal-btn')
        closeButton?.setAttribute('aria-label', 'Close dialog')
        root?.querySelector('button:not(:disabled), input:not(:disabled), textarea:not(:disabled), select:not(:disabled), a[href]')?.focus()
    } else if (previousFocus?.isConnected) previousFocus.focus()
}, { immediate: true })

onBeforeUnmount(() => document.removeEventListener('keydown', handleKeydown, true))
</script>

<template>
    <UModal ref="modal" v-bind="$attrs" :model-value="modelValue" :title="title"
        role="dialog" :aria-modal="modelValue ? 'true' : undefined" :aria-label="title"
        @update:model-value="emit('update:modelValue', $event)" @cancel="emit('cancel')" @ok="emit('ok')">
        <template v-for="(_, name) in $slots" #[name]="slotProps">
            <slot :name="name" v-bind="slotProps || {}" />
        </template>
    </UModal>
</template>
